import { useRef, useEffect, useState } from "react";
import { initSprites, drawSprite } from "./Sprite"; // Assuming initSprites and drawSprite handle canvas

const Game = () => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState("Splash"); // "Splash", "Game", "Score"
  const [birdPosition, setBirdPosition] = useState({ x: 60, y: 0 });
  const [pipes, setPipes] = useState([]);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(localStorage.getItem("best") || 0);
  const [frames, setFrames] = useState(0); // Tracks game frames
  const [fgpos, setFgpos] = useState(0);

  const handleClick = () => {
    if (gameState === "Splash") {
      setGameState("Game"); // Start the game
    } else if (gameState === "Game") {
      setBirdPosition((prevPosition) => ({
        ...prevPosition,
        y: prevPosition.y - 30, // Bird jump
      }));
    } else if (gameState === "Score") {
      setGameState("Splash"); // Reset game
      setScore(0);
      setPipes([]);
      setBirdPosition({ x: 60, y: 0 }); // Reset bird position
    }
  };

  useEffect(() => {
    const drawGame = (ctx, sprites) => {
      // Ensure sprites are loaded
      if (!sprites) return;

      // Clear the canvas
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      // Draw background
      drawSprite(
        ctx,
        sprites.bg,
        0,
        canvasRef.current.height - sprites.bg.height
      );
      drawSprite(
        ctx,
        sprites.bg,
        sprites.bg.width,
        canvasRef.current.height - sprites.bg.height
      );

      // Draw foreground
      drawSprite(
        ctx,
        sprites.fg,
        fgpos,
        canvasRef.current.height - sprites.fg.height
      );
      drawSprite(
        ctx,
        sprites.fg,
        fgpos + sprites.fg.width,
        canvasRef.current.height - sprites.fg.height
      );

      // Draw bird
      drawSprite(ctx, sprites.bird[0], birdPosition.x, birdPosition.y);

      // Draw pipes
      pipes.forEach((pipe) => {
        drawSprite(ctx, sprites.pipeNorth, pipe.x, pipe.y); // Upper pipe
        drawSprite(
          ctx,
          sprites.pipeSouth,
          pipe.x,
          pipe.y + 80 + sprites.pipeNorth.height
        ); // Lower pipe
      });

      // Display score
      ctx.font = "30px Arial";
      ctx.fillStyle = "#fff";
      ctx.fillText(`Score: ${score}`, 10, 50);

      // Handle different game states
      if (gameState === "Splash" && sprites?.text?.GetReady) {
        // Draw splash screen elements
        drawSprite(
          ctx,
          sprites.text.GetReady,
          canvasRef.current.width / 2 - sprites.text.GetReady.width / 2,
          100
        );
      } else if (gameState === "Score" && sprites?.text?.GameOver) {
        // Draw game over and score
        drawSprite(
          ctx,
          sprites.text.GameOver,
          canvasRef.current.width / 2 - sprites.text.GameOver.width / 2,
          100
        );
        ctx.fillText(
          `Best: ${bestScore}`,
          canvasRef.current.width / 2 - 50,
          150
        );
      }
    };

    const updateGame = () => {
      setFrames((prevFrames) => prevFrames + 1); // Increment frame count

      if (gameState !== "Score") {
        setFgpos((prevFgpos) => (prevFgpos - 2) % 14); // Move foreground

        if (gameState === "Game") {
          // Game logic: update bird, pipes, and score
          setBirdPosition((prevPosition) => ({
            ...prevPosition,
            y: prevPosition.y + 2, // Apply gravity
          }));

          setPipes((prevPipes) => {
            // Move pipes and update positions
            const newPipes = prevPipes.map((pipe) => ({
              ...pipe,
              x: pipe.x - 2, // Move pipes left
            }));

            // Spawn new pipes based on frame count
            if (frames % 100 === 0) {
              const newPipe = {
                x: canvasRef.current.width,
                y: Math.random() * -200, // Random height
              };
              return [...newPipes, newPipe]; // Add new pipe
            }
            return newPipes;
          });

          // Update score
          setScore((prevScore) => prevScore + 1); // Example: Increment score each frame
        }
      } else if (gameState === "Score") {
        if (score > bestScore) {
          setBestScore(score);
          localStorage.setItem("best", score); // Save best score in localStorage
        }
      }
    };

    const startGame = (ctx, sprites) => {
      const loop = () => {
        updateGame();
        drawGame(ctx, sprites);
        window.requestAnimationFrame(loop);
      };
      window.requestAnimationFrame(loop);
    };

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.src = "/sheet.png"; // Ensure this path is correct.

    img.onload = () => {
      const loadedSprites = initSprites(img);
      startGame(ctx, loadedSprites);
    };
  }, [birdPosition, pipes, score, bestScore, frames, gameState, fgpos]); // Dependencies for game logic and rendering

  return (
    <div onClick={handleClick}>
      <canvas
        ref={canvasRef}
        width={320}
        height={480}
        style={{ border: "1px solid black", display: "block", margin: "auto" }}
      />
    </div>
  );
};

export default Game;
